import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { JsonFailure } from '@skynet/data-ws-core';
import { registerErrorMessage, parseResponse } from '@skynet/data-ws-core';
import { deleteApiKey, listen, listenSubAccounts } from './actions';
import { actionOnMessage } from '@skynet/data-ws-mm';
import type {
  MainAccountsResponse,
  SubAccountOkResponse,
  SubAccountsResponse,
  SubAccountsState,
  SupportedOperationsResponse,
} from './types';
import { normalizeAccount, normalizeSupportedOperations } from './transformations';
import { SubAccountCommands } from './types';
import { hasProperty, isRecord, isString } from '@skynet/type-guards';

/**
 * This slice's own frames, and the one mutation it tracks. The other API key
 * commands belong to the modals of the apiKeys slice, failures included.
 *
 * Deliberately excludes the pre-Global.Clients#711 (SKY-3540 v1) `Accounts`/
 * `Balances` commands this section used to answer `Listen` with: those names
 * are also Loans'/Retainers'/the shared accounts factory's own commands, and
 * `allowedCommands` gates their *error* frames too (`parseResponse` drops an
 * error whose `errorCommand` isn't in the list) — widening it here would leak
 * an unrelated Loans/Retainers `Accounts`/`Balances` failure onto this page.
 * {@link isLegacyFrame} instead reads the already-parsed `json` `parseResponse`
 * hands back even for a command it skips.
 */
const allowedCommands = Object.values(SubAccountCommands);

/**
 * The old `Accounts`/`Balances` envelope of this section, `{ mainAccounts,
 * subAccounts, apiKeys }` — distinguished from an `Accounts`/`Balances` frame
 * of another section (each has its own) by the `mainAccounts` key only this
 * section's frames ever carried.
 */
function isLegacyFrame(json: unknown): boolean {
  return (
    hasProperty(json, 'command') &&
    isString(json.command) &&
    (json.command === 'Accounts' || json.command === 'Balances') &&
    hasProperty(json, 'mainAccounts')
  );
}

const describeFailure = (json: JsonFailure) =>
  typeof json.message === 'string' && json.message !== ''
    ? json.message
    : 'Sorry, an unexpected error has occurred';

registerErrorMessage(SubAccountCommands.DeleteAPIKey, {
  title: 'Delete API Key Error',
  getDescription: describeFailure,
});
registerErrorMessage(SubAccountCommands.MainAccounts, {
  title: 'Accounts Error',
  getDescription: describeFailure,
});
registerErrorMessage(SubAccountCommands.SubAccounts, {
  title: 'Sub-accounts Error',
  getDescription: describeFailure,
});

/**
 * The frame's `list` is iterated downstream. A `null` or missing list is an
 * empty one (see `JsonList`) — rejecting it would drop the whole frame and
 * leave the page loading forever — but any other non-array would break the
 * iteration, so it rejects the frame.
 */
function hasAccountList(json: object): boolean {
  const list: unknown = hasProperty(json, 'list') ? json.list : undefined;
  return list === null || list === undefined || Array.isArray(list);
}

function isMainAccountsResponse(json: unknown): json is MainAccountsResponse {
  return (
    hasProperty(json, 'command') &&
    json.command === SubAccountCommands.MainAccounts &&
    hasAccountList(json)
  );
}

/** Without a numeric `mainAccountID` the list cannot be told whose it is. */
function isSubAccountsResponse(json: unknown): json is SubAccountsResponse {
  return (
    hasProperty(json, 'command') &&
    json.command === SubAccountCommands.SubAccounts &&
    hasProperty(json, 'mainAccountID') &&
    typeof json.mainAccountID === 'number' &&
    hasAccountList(json)
  );
}

/**
 * A `SupportedOperations` frame, whatever its shape. Matched on `command`
 * alone: the frame names no section, and only `api-keys` sends it today. A
 * section that starts sending the same command would overwrite these flags,
 * so it would need a discriminator (e.g. a `section` field) checked here.
 */
function isSupportedOperationsFrame(json: unknown): boolean {
  return hasProperty(json, 'command') && json.command === SubAccountCommands.SupportedOperations;
}

function isSupportedOperationsResponse(json: unknown): json is SupportedOperationsResponse {
  return (
    isSupportedOperationsFrame(json) &&
    hasProperty(json, 'perExchange') &&
    isRecord(json.perExchange)
  );
}

export const initialState: SubAccountsState = {
  requestStatus: null,
  error: null,

  mainAccounts: [],
  subAccounts: {},
  subAccountsStatus: {},

  supportedOperations: {},

  filters: { search: '', exchanges: [] },

  resRemove: null,
};

const subAccountsSlice = createSlice({
  name: 'subAccounts',
  initialState,
  reducers: {
    setSearch: (state, action: PayloadAction<string>) => {
      state.filters.search = action.payload;
    },
    setExchangeFilter: (state, action: PayloadAction<number[]>) => {
      state.filters.exchanges = action.payload;
    },
    resetFilters: (state) => {
      state.filters = { search: '', exchanges: [] };
    },
    resetRemoveStatus: (state) => {
      state.resRemove = null;
    },
  },
  extraReducers: (builder) => {
    // Listen
    builder.addCase(listen, (state, action) => {
      const { isInitCall } = action.meta;
      if (isInitCall) {
        state.requestStatus = 'pending';
        // Until the section's own frame arrives, nothing is offered: flags of
        // a previous visit could offer what the backend no longer allows.
        state.supportedOperations = {};
        // Sub-accounts are asked for again as their main accounts unfold: the
        // ones of a previous visit, or of before a reconnect, may be stale.
        state.subAccounts = {};
        state.subAccountsStatus = {};
      }
    });

    // A repeated request keeps the sub-accounts it has on screen meanwhile.
    builder.addCase(listenSubAccounts, (state, action) => {
      state.subAccountsStatus[String(action.payload.mainAccountID)] = 'pending';
    });

    // The create/edit/copy requests are tracked by the apiKeys slice, which
    // owns their modals; only the delete, confirmed in the list, is tracked here.
    builder.addCase(deleteApiKey.pending, (state) => {
      state.resRemove = 'pending';
    });

    // WS message handling
    builder.addCase(actionOnMessage, (state, action) => {
      const { json, skipProcessing, error, errorCommand } = parseResponse<
        | MainAccountsResponse
        | SubAccountsResponse
        | SupportedOperationsResponse
        | SubAccountOkResponse
      >(action.payload, allowedCommands);

      // A backend that hasn't picked up Global.Clients#711 (SKY-3540 v2) yet
      // still answers this section's `Listen` with the old `Accounts`/
      // `Balances` envelope — a command `allowedCommands` doesn't recognize,
      // which `parseResponse` marks `skipProcessing` but still hands back
      // parsed, below. Recognized here only to log it: otherwise nothing
      // would say why the loader is stuck in `pending` forever.
      if (skipProcessing && isLegacyFrame(json)) {
        console.error(
          'subAccounts: received the old Accounts/Balances contract for the api-keys section — the backend has not deployed Global.Clients#711 (SKY-3540 v2) yet',
        );
        return;
      }

      if (skipProcessing || !json) return;

      // Matched on the error command, which is set whether or not a message is
      // registered for it, so no request is left pending by a missing message.
      const failedDelete = errorCommand === SubAccountCommands.DeleteAPIKey;
      const failedLoad = errorCommand === SubAccountCommands.MainAccounts;
      const failedSubLoad = errorCommand === SubAccountCommands.SubAccounts;
      if (errorCommand !== undefined) {
        if (error) state.error = error;
        if (failedDelete && state.resRemove === 'pending') state.resRemove = 'error';
        // An error frame names no section, so only a load of ours in flight is
        // released — one that would otherwise keep the page on its loader.
        if (failedLoad && state.requestStatus === 'pending') state.requestStatus = 'error';
        // Nor does it name the main account, so every request in flight is
        // released: each can be asked for again.
        if (failedSubLoad) {
          for (const [id, status] of Object.entries(state.subAccountsStatus)) {
            if (status === 'pending') state.subAccountsStatus[id] = 'error';
          }
        }
        return;
      }

      const { command } = json;

      if (isMainAccountsResponse(json)) {
        state.mainAccounts = (json.list ?? []).map(normalizeAccount);
        state.requestStatus = 'success';
        return;
      }

      if (isSubAccountsResponse(json)) {
        const { mainAccountID } = json;
        state.subAccounts[String(mainAccountID)] = (json.list ?? []).map((account) => ({
          ...normalizeAccount(account),
          mainAccountID,
        }));
        state.subAccountsStatus[String(mainAccountID)] = 'success';
        return;
      }

      // A malformed frame fails closed: keeping the flags of an earlier one
      // could offer what the backend has since withdrawn.
      if (isSupportedOperationsFrame(json)) {
        state.supportedOperations = isSupportedOperationsResponse(json)
          ? normalizeSupportedOperations(json)
          : {};
        return;
      }

      // Every other ok (create, edit, copy) is handled by the apiKeys slice.
      if (command === 'ok' && json.okCommand === SubAccountCommands.DeleteAPIKey) {
        state.resRemove = 'success';
      }
    });
  },
});

export const subAccountsAction: typeof subAccountsSlice.actions & {
  listen: typeof listen;
  listenSubAccounts: typeof listenSubAccounts;
  deleteApiKey: typeof deleteApiKey;
} = {
  ...subAccountsSlice.actions,
  listen,
  listenSubAccounts,
  deleteApiKey,
};

export default subAccountsSlice.reducer;
